import api from './api';

// ---------------------------------------------------------------------------
// قرعه‌کشی وزنی کاربران — /admin/raffle (مستقل از قرعه‌کشی مایلستونی /admin/lottery)
// ---------------------------------------------------------------------------

export type RaffleStatus = 'draft' | 'drawn' | 'archived';
export type RaffleRuleType = 'app_installed' | 'signup_range' | 'min_purchases' | 'recent_activity';

export interface RaffleRule {
  type: RaffleRuleType;
  multiplier: number;
  enabled: boolean;
  params?: {
    from?: string;
    to?: string;
    minCount?: number;
    days?: number;
  };
}

export interface RafflePrize {
  rank: number;
  title: string;
  amount?: number;
}

export interface RaffleWinner {
  rank: number;
  userId: string;
  name: string;
  phoneNumber?: string;
  weight: number;
  matchedRules: RaffleRuleType[];
  prizeTitle: string;
  prizeAmount?: number;
}

export interface RaffleOptions {
  /** ادمین‌ها در قرعه‌کشی نباشند (پیش‌فرض: روشن) */
  excludeAdmins?: boolean;
  /** فروشنده‌ها (صاحبان فروشگاه) در قرعه‌کشی نباشند (پیش‌فرض: روشن) */
  excludeSellers?: boolean;
  /** نمایش تعداد شرکت‌کننده‌ها در صحنهٔ نمایش (پیش‌فرض: خاموش) */
  showParticipants?: boolean;
}

export interface RaffleRedraw {
  rank: number;
  removedUserId: string;
  removedName: string;
  newUserId: string;
  newName: string;
  at: string;
  by: string;
}

export interface RaffleItem {
  _id: string;
  title: string;
  description?: string;
  status: RaffleStatus;
  pool?: { signupFrom?: string; signupTo?: string };
  rules: RaffleRule[];
  options?: RaffleOptions;
  /** همیشه = prizes.length (سرور محاسبه می‌کند) */
  winnersCount: number;
  prizes: RafflePrize[];
  draw?: {
    algorithm: string;
    seedHash: string;
    seed?: string;
    drawnAt: string;
    drawnBy?: { _id: string; name?: string } | string;
    participants: number;
    totalWeight: number;
  };
  winners: RaffleWinner[];
  redraws?: RaffleRedraw[];
  createdBy?: { _id: string; name?: string } | string;
  createdAt: string;
  updatedAt: string;
}

export interface RaffleInput {
  title: string;
  description?: string;
  pool?: { signupFrom?: string | null; signupTo?: string | null };
  rules: RaffleRule[];
  options?: RaffleOptions;
  /** ترتیب لیست = رتبه؛ تعداد برنده = تعداد جایزه */
  prizes: Array<{ title: string; amount?: number }>;
}

export interface RafflePoolPreview {
  participants: number;
  totalWeight: number;
  boostedCount: number;
  baseCount: number;
  maxWeight: number;
  ruleBreakdown: Array<{ type: RaffleRuleType; multiplier: number; matched: number }>;
}

export interface RaffleDrawResult {
  raffle: RaffleItem;
  candidatesSample: string[];
}

export interface PaginatedRaffles {
  items: RaffleItem[];
  total: number;
  page: number;
  pages: number;
}

export const RAFFLE_STATUS_LABELS: Record<RaffleStatus, string> = {
  draft: 'پیش‌نویس',
  drawn: 'انجام‌شده',
  archived: 'آرشیو',
};

export const RAFFLE_RULE_LABELS: Record<RaffleRuleType, string> = {
  app_installed: 'نصب اپلیکیشن',
  signup_range: 'ثبت‌نام در بازهٔ تاریخ',
  min_purchases: 'حداقل خرید تحویل‌شده',
  recent_activity: 'فعالیت اخیر',
};

export const RAFFLE_RULE_DESCRIPTIONS: Record<RaffleRuleType, string> = {
  app_installed: 'کاربرانی که اپلیکیشن موبایل را نصب و استفاده کرده‌اند',
  signup_range: 'کاربرانی که بین دو تاریخ مشخص ثبت‌نام کرده‌اند',
  min_purchases: 'کاربرانی که حداقل N سفارش تحویل‌شده دارند',
  recent_activity: 'کاربرانی که در N روز اخیر فعال بوده‌اند',
};

/** api.post/put پاکت { status, data } را باز نمی‌کنند؛ هر دو حالت را پوشش می‌دهیم */
const unwrap = <T>(res: any): T => {
  if (res && typeof res === 'object' && 'status' in res && 'data' in res) return res.data as T;
  return res as T;
};

const raffleService = {
  async list(query: { page?: number; limit?: number; search?: string; status?: string } = {}): Promise<PaginatedRaffles> {
    const params: any = {};
    if (query.page) params.page = query.page;
    if (query.limit) params.limit = query.limit;
    if (query.search) params.search = query.search;
    if (query.status) params.status = query.status;
    const raw: any = await api.get<any>('/admin/raffle', { params } as any);
    return {
      items: Array.isArray(raw?.items) ? raw.items : [],
      total: Number(raw?.total || 0),
      page: Number(raw?.page || query.page || 1),
      pages: Number(raw?.pages || 1),
    };
  },

  async get(id: string): Promise<RaffleItem> {
    const raw: any = await api.get<any>(`/admin/raffle/${id}`);
    return unwrap<RaffleItem>(raw);
  },

  async create(input: RaffleInput): Promise<RaffleItem> {
    const res = await api.post<any>('/admin/raffle', input);
    return unwrap<RaffleItem>(res);
  },

  async update(id: string, input: RaffleInput): Promise<RaffleItem> {
    const res = await api.put<any>(`/admin/raffle/${id}`, input);
    return unwrap<RaffleItem>(res);
  },

  async remove(id: string): Promise<void> {
    await api.delete<any>(`/admin/raffle/${id}`);
  },

  async archive(id: string): Promise<RaffleItem> {
    const res = await api.post<any>(`/admin/raffle/${id}/archive`);
    return unwrap<RaffleItem>(res);
  },

  async poolPreview(id: string): Promise<RafflePoolPreview> {
    const raw: any = await api.get<any>(`/admin/raffle/${id}/pool-preview`);
    return unwrap<RafflePoolPreview>(raw);
  },

  async candidatesSample(id: string): Promise<string[]> {
    const raw: any = await api.get<any>(`/admin/raffle/${id}/candidates-sample`);
    const data = unwrap<{ names: string[] }>(raw);
    return Array.isArray((data as any)?.names) ? (data as any).names : [];
  },

  async draw(id: string): Promise<RaffleDrawResult> {
    const res = await api.post<any>(`/admin/raffle/${id}/draw`);
    return unwrap<RaffleDrawResult>(res);
  },

  /** رد برندهٔ فعلی رتبه و انتخاب جایگزین از ادامهٔ همان ترتیب seed */
  async redraw(id: string, rank: number): Promise<{ raffle: RaffleItem; newWinner: RaffleWinner }> {
    const res = await api.post<any>(`/admin/raffle/${id}/redraw/${rank}`);
    return unwrap<{ raffle: RaffleItem; newWinner: RaffleWinner }>(res);
  },
};

export default raffleService;
